/**
 * User identification manager for B2M Tracker.
 *
 * Identity is a random per-install salt, validated on every visit against a
 * stored vector of device components (see device-fingerprint.js). The salt is
 * what keeps two identical devices apart; the component vector is what lets a
 * returning user be recognised after their signals drift.
 *
 * Resolution is synchronous whenever storage is writable, so the first event of
 * the page already carries the final user_id. The async init() only computes
 * the deterministic device-class hash, which is a diagnostic signal and the
 * last-resort identity for contexts where nothing can be persisted.
 */
import {
  RECORD_VERSION,
  collectComponents,
  hashComponents,
  compareComponents,
  generateSalt,
  isValidRecord
} from './device-fingerprint.js';

export default class UserID {
  constructor(storage, logger = null) {
    this.storage = storage;
    this.logger = logger;
    this.id = null;
    this.fingerprintId = null;
    this.externalId = null;
    this.isReady = false;
    // True while `this.id` is a throwaway random id (set before a stored/shared
    // id is found or the fingerprint resolves). Drives id_status:'temporary'.
    this.idIsTemporary = true;

    // Deterministic hash of device-class signals. Collides across identical
    // devices by construction, so it is NOT the identity — it is exposed for
    // diagnostics and used as the identity only when storage is unavailable.
    this.deviceClassHash = null;

    // Diagnostics from the last device-record comparison.
    this.matchInfo = null;
    // Set when a stored record was REJECTED and a fresh identity minted in its
    // place — the one moment the SDK knows both the old and the new id. The
    // tracker consumes this once and emits an identity_reset event, giving the
    // backend a deterministic old→new bridge without any server-side guessing.
    this.pendingReset = null;
    // Set when identity had to fall back to the deterministic hash because
    // nothing can be persisted (3rd-party iframe with storage blocked).
    this.usingDeterministicFallback = false;

    // Identity source ('salt' | 'deviceClass') + optional resolve service —
    // injected via configure() before any resolution that can mint an id.
    this.identitySource = 'salt';
    this.identityResolveUrl = null;
    this.identityResolveTimeoutMs = 800;
    // Which mode wrote the adopted device record ('salt' | 'dc'), so
    // deviceClass mode can tell a record it owns (adopt as-is — it may carry a
    // server-disambiguated id) from a salt-mode record (migrate once).
    this._recordSource = null;
    // Old id from a REJECTED record seen in deviceClass mode — bridged in
    // _adoptDeviceClassIdentity(), where the new id is finally known.
    this._dcPreviousId = null;

    // Restore externally-set user ID (from a previous session) if present.
    // Sanitized: an older SDK could have persisted the literal "undefined".
    this.externalId = UserID.sanitizeExternalId(this.storage.get('b2m_external_user_id'));

    // Try to adopt an existing identity right away. Minting a NEW one is
    // deferred until adoptStoredId(), because cross-subdomain cookie sharing is
    // not configured yet at construction time and the identity we are about to
    // create may already exist on the parent domain.
    this._resolveIdentity({ allowMint: false });
  }

  _log(...args) {
    if (this.logger && typeof this.logger.log === 'function') this.logger.log(...args);
  }

  /**
   * Inject identity settings from config. Must run before adoptStoredId() —
   * deviceClass mode must never mint a salt, and the constructor's own
   * resolution pass runs with allowMint:false, so calling this right after
   * config.init() is early enough.
   */
  configure({ identitySource, identityResolveUrl, identityResolveTimeoutMs } = {}) {
    if (identitySource === 'deviceClass' || identitySource === 'salt') {
      this.identitySource = identitySource;
    }
    if (identityResolveUrl !== undefined) this.identityResolveUrl = identityResolveUrl;
    if (identityResolveTimeoutMs) this.identityResolveTimeoutMs = identityResolveTimeoutMs;
  }

  /**
   * A usable external id, or null. Filters the junk shapes real integrations
   * produce on logout — null/undefined/'' and the literal "undefined"/"null"
   * strings (templating artifacts) — both on write and when restoring a
   * value an older SDK version may have persisted.
   */
  static sanitizeExternalId(value) {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null') return null;
    return s;
  }

  /**
   * Set a customer-supplied user ID (e.g. their internal user PK).
   * Persisted in localStorage so the same identity is used across sessions.
   * Pass null/undefined/empty (or their string artifacts) to clear.
   */
  setExternalId(externalId) {
    const clean = UserID.sanitizeExternalId(externalId);
    if (!clean) {
      this.externalId = null;
      this.storage.remove('b2m_external_user_id');
      return;
    }
    this.externalId = clean;
    this.storage.set('b2m_external_user_id', this.externalId);
  }

  getExternalId() {
    return this.externalId;
  }

  /**
   * Re-resolve identity once cross-subdomain storage (cookieDomain) is active.
   * The constructor runs before that, so on a fresh subdomain it cannot see the
   * device record the parent/apex domain already wrote to the shared cookie.
   * Running again here means the very first events carry the shared user_id
   * instead of a newly minted one that would double-count the visitor.
   */
  adoptStoredId() {
    this._resolveIdentity({ allowMint: true });
    const ext = UserID.sanitizeExternalId(this.storage.get('b2m_external_user_id'));
    if (ext) this.externalId = ext;
  }

  /**
   * Resolve the device identity from storage, or mint a new one.
   *
   * Order matters: an existing device record wins over the legacy user id,
   * which wins over minting, so an upgrading visitor keeps their id instead of
   * churning into a new one.
   *
   * @param {{allowMint:boolean}} options
   */
  _resolveIdentity({ allowMint }) {
    if (!this.idIsTemporary && this.id) return; // already resolved

    const components = collectComponents();
    const current = hashComponents(components);
    const record = this.storage.getDeviceRecord();
    let rejected = false;

    if (isValidRecord(record)) {
      const match = compareComponents(record.c, current);
      this.matchInfo = match;

      if (match.isMatch) {
        this.id = record.i;
        this.fingerprintId = record.i;
        this.idIsTemporary = false;
        this._recordSource = record.s || 'salt';
        // Re-baseline the stored vector to the values seen right now. Without
        // this, drift accumulates visit after visit until the score finally
        // falls below the threshold and the user splits in two.
        this._writeRecord(record.i, current, {
          createdAt: record.t,
          drifted: match.changed.length > 0 ? (record.d || 0) + 1 : (record.d || 0),
          source: record.s
        });
        if (match.changed.length) {
          this._log('[B2M SDK] Device matched with drift:', match.changed.join(','),
            `(score ${match.score.toFixed(2)})`);
        }
        return;
      }

      rejected = true;
      this._log('[B2M SDK] Stored device record rejected — changed:',
        match.changed.join(',') || 'none',
        `(score ${match.score.toFixed(2)}, anchors ${match.anchorMismatches})`);
    }

    if (!allowMint) {
      // No usable record yet and it is too early to create one. Keep tracking
      // alive on a throwaway id; events are held until this resolves.
      if (!this.id) {
        this.id = this._generateQuickId();
        this.fingerprintId = this.id;
      }
      this.idIsTemporary = true;
      return;
    }

    // deviceClass mode: the identity is the deterministic device-class hash,
    // resolved (and optionally server-disambiguated) asynchronously in init().
    // Never mint a salt. Events are held by the same temp-id machinery that
    // covers every other async resolution. Remember a rejected record's id so
    // init() can declare the old→new bridge once the hash is known.
    if (this.identitySource === 'deviceClass') {
      // Any pre-existing identity (rejected record, legacy b2m_user_id) is
      // bridged in init() — including the legacy key, which salt mode would
      // have reused but this mode replaces with the hash.
      this._dcPreviousId =
        (record && typeof record.i === 'string' && record.i) ||
        this.storage.getUserId() || null;
      if (!this.id) {
        this.id = this._generateQuickId();
        this.fingerprintId = this.id;
      }
      this.idIsTemporary = true;
      return;
    }

    // Nothing can be persisted (3rd-party iframe with storage blocked): a fresh
    // salt would be forgotten on every page load and mint a new "user" each
    // time. Fall back to the deterministic device-class hash — it collides
    // across identical devices, but a merged user beats a new user per pageview.
    if (this.storage.isUsingMemoryFallback()) {
      this.usingDeterministicFallback = true;
      this.idIsTemporary = true; // resolved asynchronously in init()
      this._log('[B2M SDK] Storage unavailable — identity falls back to the device-class hash');
      return;
    }

    // A record from an older SDK version, or the plain id key written before
    // device records existed. Reuse it: continuity is worth more than the clean
    // per-install salt we would otherwise mint.
    //
    // Known race: two tabs doing a first-ever visit simultaneously can each
    // mint a different salt (read-mint-write is synchronous, so the window is
    // sub-millisecond, but it exists). Last write wins in storage; the losing
    // tab keeps its id in memory for its lifetime and converges on the next
    // load. Accepted: it needs two simultaneous FIRST visits, and the fallout
    // is one tab's worth of events under a one-off id.
    //
    // Only when nothing was REJECTED, though. A rejected record means this is a
    // different device, and b2m_user_id still holds the id we just refused —
    // reusing it would hand the new device the old identity and undo the check.
    const legacyId = rejected
      ? null
      : ((record && typeof record.i === 'string' && record.i) || this.storage.getUserId());
    const id = legacyId || generateSalt();

    this.id = id;
    this.fingerprintId = id;
    this.idIsTemporary = false;
    this._writeRecord(id, current, {});
    this._log('[B2M SDK] Device identity', legacyId ? 'migrated' : 'created');

    if (rejected && record && typeof record.i === 'string' && record.i) {
      this.pendingReset = {
        previousUserId: record.i,
        matchScore: this.matchInfo ? this.matchInfo.score : null,
        changedComponents: this.matchInfo ? this.matchInfo.changed.slice() : []
      };
    }
  }

  /**
   * One-shot read of a pending identity reset (old id → new id bridge).
   * Returns { previousUserId, matchScore, changedComponents } exactly once
   * after a record rejection minted a fresh identity; null otherwise.
   */
  consumePendingReset() {
    const reset = this.pendingReset;
    this.pendingReset = null;
    if (!reset || reset.previousUserId === this.id) return null;
    return reset;
  }

  _writeRecord(id, components, { createdAt, drifted, source } = {}) {
    this.storage.setDeviceRecord({
      v: RECORD_VERSION,
      i: id,
      c: components,
      t: createdAt || Date.now(),
      u: Date.now(),
      d: drifted || 0,
      ...(source ? { s: source } : {})
    });
    // Keep the standalone key in sync — other SDK versions and any external
    // integration still read b2m_user_id directly.
    this.storage.setUserId(id);
  }

  /** True when the SENT user_id is a throwaway temp (no external id and id not
   *  yet resolved/adopted). Backend uses id_status:'temporary' to stitch. */
  isIdTemporary() {
    return !this.externalId && this.idIsTemporary;
  }

  /**
   * Generate a quick random ID (synchronous)
   */
  _generateQuickId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 10);
    return `${randomPart}${timestamp}`;
  }

  async init() {
    // Normally a no-op: identity resolved synchronously in adoptStoredId().
    this._resolveIdentity({ allowMint: true });

    this.deviceClassHash = await this.generateAdvancedFingerprint();

    if (this.identitySource === 'deviceClass') {
      await this._adoptDeviceClassIdentity();
    } else if (this.usingDeterministicFallback) {
      this.id = this.deviceClassHash;
      this.fingerprintId = this.deviceClassHash;
    }

    this.idIsTemporary = false;
    this.isReady = true;
  }

  /**
   * deviceClass mode: the identity IS the device-class hash — identical in
   * every browser profile and private window of the same physical device —
   * optionally disambiguated once by the resolve service so identical devices
   * don't collapse into one profile.
   *
   * A record this mode wrote earlier (s:'dc') is adopted as-is: it may carry a
   * server-disambiguated id that a plain re-hash cannot reproduce. Any other
   * pre-existing identity (salt record, legacy id) is migrated exactly once,
   * declaring the old→new bridge through the identity_reset machinery.
   */
  async _adoptDeviceClassIdentity() {
    // Records this mode wrote: s:'dc' = locally-derived hash (stage 1),
    // s:'dcr' = server-resolved canonical id (stage 2). A 'dcr' id is final.
    // A 'dc' id is settled only while no resolve service is configured — the
    // moment stage 2 ships (identityResolveUrl set), existing browsers
    // upgrade with one round-trip on their next visit.
    const adoptedOwn = !this.idIsTemporary && !!this.id &&
      (this._recordSource === 'dc' || this._recordSource === 'dcr');
    if (adoptedOwn && (this._recordSource === 'dcr' || !this.identityResolveUrl)) return;

    const previousId = (!this.idIsTemporary && this.id) ? this.id : (this._dcPreviousId || null);
    this._dcPreviousId = null;

    const serverId = await this._resolveIdentityWithService(
      adoptedOwn ? this.id : this.deviceClassHash
    );
    const resolved = serverId || (adoptedOwn ? this.id : this.deviceClassHash);
    // Only a successful server answer settles stage 2 ('dcr'); a failed call
    // leaves the record at 'dc' so the upgrade retries on a later visit.
    const source = serverId ? 'dcr' : 'dc';

    if (previousId && previousId !== resolved) {
      this.pendingReset = {
        previousUserId: previousId,
        matchScore: null,
        changedComponents: ['identity_source']
      };
    }

    this.id = resolved;
    this.fingerprintId = resolved;
    if (!this.storage.isUsingMemoryFallback()) {
      this._writeRecord(resolved, hashComponents(collectComponents()), { source });
    }
    this._recordSource = source;
    this._log('[B2M SDK] deviceClass identity adopted',
      previousId && previousId !== resolved ? `(migrated from ${previousId})` : '');
  }

  /**
   * One-shot disambiguation round-trip (deviceClass mode, first boot in this
   * browser profile only). The service maps { fingerprint_id, proposed_id } to
   * the canonical id for this physical device — telling identical devices
   * apart with server-side signals (e.g. source IP) that no client can see.
   * Fail-soft: no endpoint / timeout / error → null, caller uses the hash.
   */
  async _resolveIdentityWithService(proposedId) {
    if (!this.identityResolveUrl) return null;
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = setTimeout(() => { if (controller) controller.abort(); },
        this.identityResolveTimeoutMs || 800);
      const response = await fetch(this.identityResolveUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fingerprint_id: this.deviceClassHash || proposedId,
          proposed_id: proposedId
        }),
        ...(controller ? { signal: controller.signal } : {})
      });
      clearTimeout(timer);
      if (!response.ok) return null;
      const data = await response.json();
      return (data && typeof data.user_id === 'string' && data.user_id) ? data.user_id : null;
    } catch (e) {
      this._log('[B2M SDK] identity resolve service unavailable — using local hash:', e && e.message);
      return null;
    }
  }

  getId() {
    // External (customer-supplied) ID always wins — this is the identity
    // the customer's backend already knows about, so experiments and events
    // should be keyed on it whenever it is available.
    return this.externalId || this.id;
  }

  /**
   * The stable device identity, independent of who is logged in. Experiment
   * bucketing keys on this so assignments survive login/logout and cannot be
   * inherited from a sibling subdomain's logged-in user.
   */
  getFingerprintId() {
    return this.fingerprintId;
  }

  /**
   * Deterministic hash of device-class signals. Identical devices produce the
   * same value, so this is a stitching hint for the backend, never an identity.
   * Null until init() resolves.
   */
  getDeviceClassHash() {
    return this.deviceClassHash;
  }

  /** Last device-record comparison result — for debugging identity churn. */
  getDeviceMatchInfo() {
    return this.matchInfo;
  }

  /**
   * Deterministic device-class fingerprint.
   * Uses hardware + locale signals that stay stable across browsers on the same
   * device. Browser-specific signals (canvas, WebGL, audio, fonts, plugins) are
   * intentionally excluded so the same physical device produces the same value
   * in Chrome, Firefox, etc.
   *
   * The component list and its order are frozen: this value is the identity for
   * storage-blocked contexts, so changing it would re-mint every such visitor.
   */
  generateAdvancedFingerprint = async () => {
    const intl = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions(); } catch (e) { return {}; }
    })();
    const uaData = navigator.userAgentData;

    const components = [
      // Display
      screen.width + 'x' + screen.height,
      screen.availWidth + 'x' + screen.availHeight,
      screen.colorDepth,
      screen.pixelDepth,
      window.devicePixelRatio,

      // Hardware
      navigator.hardwareConcurrency,
      navigator.maxTouchPoints,

      // Locale (IANA timezone — DST-stable, unlike getTimezoneOffset())
      intl.timeZone,
      intl.locale,
      (navigator.languages && navigator.languages.join(',')) || navigator.language,

      // Modern UA hints (Chromium); undefined in Firefox/Safari and dropped by the filter
      uaData && uaData.platform,
      uaData && typeof uaData.mobile === 'boolean' ? String(uaData.mobile) : undefined,
    ].filter(v => v !== undefined && v !== null && v !== '');

    return await this.hashString(components.join('||'));
  }

  /**
   * Hash string using crypto API or fallback
   */
  async hashString(str) {
    try {
      if (crypto.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
      }
    } catch (e) {
      // Fallback to simple hash
    }

    // Fallback simple hash for older browsers
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).padStart(8, '0');
  }
}

/**
 * Generate a hash from a string (legacy export for compatibility)
 */
export const hashString = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return hash.toString(36)
}
